"use client";

import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, Button, Container, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useMemo } from "react";
import SalifyLink from "src/lib/components/link/salify-link";
import { toAssetImages } from "src/lib/utils";
import {
  LayoutViewMode,
  ResponsivePropsConfig,
  resolveResponsiveProps,
  splitDesktopAndResponsive,
} from "src/lib/utils/responsive-props";

export type CtaButtonStyle = "primary" | "light";

export type CtaButtonItem = {
  text: string;
  link: string;
  style?: CtaButtonStyle;
  showPlayIcon?: boolean;
};

type CtaViewportData = {
  backgroundImage?: string;
  title?: string;
  buttons?: CtaButtonItem[];
};

export interface VariantProps extends CtaViewportData {
  responsive?: ResponsivePropsConfig<CtaViewportData>;
}

const ACCENT = "#FFC107";

const useLayoutViewMode = (): LayoutViewMode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "laptop"));
  if (isMobile) return "mobile";
  if (isTablet) return "tablet";
  return "desktop";
};

const CtaButton = ({ item, index }: { item: CtaButtonItem; index: number }) => {
  const isPrimary = (item.style || (index === 0 ? "primary" : "light")) === "primary";
  const showPlay = Boolean(item.showPlayIcon && isPrimary);

  return (
    <SalifyLink href={item.link || "#"}>
      <Button
        variant="contained"
        startIcon={showPlay ? <PlayArrowIcon /> : undefined}
        sx={{
          bgcolor: isPrimary ? ACCENT : "#fff",
          color: "#111",
          fontWeight: 600,
          px: { xs: 2.5, laptop: 3 },
          py: 1.15,
          borderRadius: 1,
          boxShadow: isPrimary ? "none" : "0 1px 4px rgba(0,0,0,0.12)",
          border: isPrimary ? "none" : "1px solid rgba(255,255,255,0.8)",
          minWidth: { xs: "100%", tablet: 180 },
          "&:hover": {
            bgcolor: isPrimary ? "#e6ad06" : "#f5f5f5",
            boxShadow: isPrimary ? "none" : "0 2px 8px rgba(0,0,0,0.15)",
          },
        }}
      >
        {item.text}
      </Button>
    </SalifyLink>
  );
};

const CtaDefaultV2 = (props: VariantProps) => {
  const viewMode = useLayoutViewMode();
  const { desktop, responsive } = splitDesktopAndResponsive(
    props as unknown as Record<string, unknown>,
  );

  const data = useMemo((): CtaViewportData => {
    const resolved = resolveResponsiveProps(
      {
        backgroundImage: (desktop.backgroundImage as string) || "",
        title: (desktop.title as string) || "",
        buttons: (desktop.buttons as CtaButtonItem[]) || [],
      },
      responsive,
      viewMode,
      ["backgroundImage", "title", "buttons"],
    );

    return {
      backgroundImage: String(resolved.backgroundImage || ""),
      title: String(resolved.title || ""),
      buttons: Array.isArray(resolved.buttons) ? resolved.buttons : [],
    };
  }, [desktop, responsive, viewMode]);

  const backgroundUrl = data.backgroundImage?.trim()
    ? toAssetImages(data.backgroundImage as string)
    : "";
  const buttons = data.buttons?.filter((item) => item?.text?.trim()) || [];

  return (
    <Box
      component="section"
      id="cta"
      sx={{
        position: "relative",
        minHeight: { xs: 240, laptop: 320 },
        display: "flex",
        alignItems: "center",
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        bgcolor: backgroundUrl ? undefined : "grey.800",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: backgroundUrl ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.55)",
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          py: { xs: 5, laptop: 6 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 3,
          }}
        >
          {data.title ? (
            <Typography
              component="h2"
              sx={{
                color: "#fff",
                fontWeight: 700,
                fontSize: { xs: 22, tablet: 28, laptop: 34 },
                lineHeight: 1.25,
                maxWidth: 900,
                textShadow: "0 2px 12px rgba(0,0,0,0.35)",
              }}
            >
              {data.title}
            </Typography>
          ) : null}

          {buttons.length > 0 ? (
            <Stack
              direction={{ xs: "column", tablet: "row" }}
              spacing={1.5}
              sx={{ width: { xs: "100%", tablet: "auto" }, maxWidth: 560 }}
            >
              {buttons.map((item, index) => (
                <Box key={`${item.text}-${index}`} sx={{ flex: { tablet: 1 } }}>
                  <CtaButton item={item} index={index} />
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
      </Container>
    </Box>
  );
};

export default CtaDefaultV2;
